// src/utils/launchRollup.js
//
// NOTE (WP6, docs/mcp-signoff-spec.md §6/§9): the AUTHORITATIVE sign-off
// verdict now lives on the proxy (proxy-server/launch/rollup.js — a verbatim
// CommonJS port of this file, plus buildRollup.js on top of it), served by
// GET /proxy/launches/<id>/rollup. LaunchPage.jsx fetches that endpoint and no
// longer calls criterionStatus()/launchRollup() from here to decide what a
// launch's verdict IS. This copy survives ONLY for:
//   - CapturePanel.jsx's live in-flight coverage tracking (utils/launchCoverage.js,
//     which imports launchRollup() for its autoDraft rollup) — a tight loop over
//     results that are not yet recorded/attached on the proxy, which the rollup
//     endpoint (reads recorded runs) cannot serve;
//   - utils/buildLaunchReport.js's HTML/print sign-off report, which stays
//     frontend-owned (DOM/print styling the proxy has no use for).
// Any RULE change here (the criterion-status logic, the rollup counts) must be
// mirrored in proxy-server/launch/rollup.js or the two copies silently drift —
// see docs/mcp-signoff-spec.md §4's shared-fixture mitigation.
//
// Pure criterion-status rollup for the Launch workspace (WP4,
// docs/launch-signoff-spec.md §7). Given one criterion (from a launch
// record's `criteria[]`, WP2 §5 shape) and a lookup of the LATEST check
// results keyed by checkId (built from a /proxy/checks/run report's
// `results[]`), decides the criterion's overall status for display and for
// the eventual sign-off rollup (WP5 reuses/mirrors this).
//
// Rules (spec §7, as implemented):
//   - mapping === 'manual'                        -> 'manual-review' (always,
//     regardless of any checks — a manual criterion is never provably "met"
//     from traffic; it's acknowledged in the sign-off flow instead, WP5)
//   - mapping === 'unmapped', or no checkIds at all -> 'not-run'
//   - checkIds present but none have a result yet   -> 'not-run' (never run,
//     or the run didn't cover this checkId)
//   - any mapped check's result.status === 'fail'    -> 'not-met'
//   - else any mapped check's result.status === 'warn' -> 'needs-review'
//   - else every mapped check's result.status === 'pass' -> 'met'
//   - else (a mix of pass/n/a/info with no fail/warn — e.g. a check that
//     came back 'n/a' because nothing in scope matched its target) ->
//     'needs-review'. The spec doesn't enumerate this case explicitly; this
//     is a deliberately conservative choice — a criterion is never reported
//     "Met" unless every one of its mapped checks actually passed.
//   - EXCEPTION to the line above: an 'n/a' from a check the compiler ATTACHED
//     (criterion.baselineIds — the whole-family conformance backstop) is
//     ignored. Those checks widen coverage beyond what the criterion named, so
//     one that didn't apply to this session is silence rather than a review
//     item; a fail/warn from them counts in full.
//
// Never throws: a malformed criterion/lookup degrades to 'not-run'.

export const CRITERION_STATUS = {
  MET: 'met',
  NOT_MET: 'not-met',
  NEEDS_REVIEW: 'needs-review',
  MANUAL_REVIEW: 'manual-review',
  NOT_RUN: 'not-run',
};

export const CRITERION_STATUS_LABEL = {
  [CRITERION_STATUS.MET]: 'Met',
  [CRITERION_STATUS.NOT_MET]: 'Not met',
  [CRITERION_STATUS.NEEDS_REVIEW]: 'Needs review',
  [CRITERION_STATUS.MANUAL_REVIEW]: 'Manual review',
  [CRITERION_STATUS.NOT_RUN]: 'Not run',
};

/**
 * resultsByCheckId: plain object OR Map, checkId -> latest result
 * ({ checkId, status, matched, found, failures, rows, ... } — the
 * evaluateCheck() shape echoed by /proxy/checks/run's `results[]`).
 */
function lookupResult(resultsByCheckId, checkId) {
  if (!resultsByCheckId || !checkId) return null;
  try {
    if (typeof resultsByCheckId.get === 'function') return resultsByCheckId.get(checkId) || null;
    return resultsByCheckId[checkId] || null;
  } catch {
    return null;
  }
}

/**
 * criterionStatus(criterion, resultsByCheckId) -> one of CRITERION_STATUS.
 * Never throws.
 */
export function criterionStatus(criterion, resultsByCheckId) {
  try {
    if (!criterion || typeof criterion !== 'object') return CRITERION_STATUS.NOT_RUN;
    if (criterion.mapping === 'manual') return CRITERION_STATUS.MANUAL_REVIEW;

    const checkIds = Array.isArray(criterion.checkIds) ? criterion.checkIds.filter(Boolean) : [];
    if (criterion.mapping === 'unmapped' || !checkIds.length) return CRITERION_STATUS.NOT_RUN;

    // Baseline checks the compiler ATTACHED (criterion.baselineIds — the
    // whole-family conformance backstop in compileCriteria.js) that came back
    // 'n/a' are dropped here. The criterion never named them; they were added
    // to widen coverage, and one that didn't apply to this session is silence,
    // not a review item. Without this, attaching a family's baseline would push
    // every broad criterion to "Needs review" on the first inapplicable check.
    // A baseline check that FAILS or WARNS still counts in full — that is the
    // entire reason for attaching it.
    const attached = new Set(Array.isArray(criterion.baselineIds) ? criterion.baselineIds : []);
    const statuses = checkIds
      .map((id) => ({ id, status: lookupResult(resultsByCheckId, id)?.status }))
      .filter((r) => r.status && !(attached.has(r.id) && r.status === 'n/a'))
      .map((r) => r.status);
    if (!statuses.length) return CRITERION_STATUS.NOT_RUN;

    if (statuses.includes('fail')) return CRITERION_STATUS.NOT_MET;
    if (statuses.includes('warn')) return CRITERION_STATUS.NEEDS_REVIEW;
    if (statuses.every((s) => s === 'pass')) return CRITERION_STATUS.MET;
    return CRITERION_STATUS.NEEDS_REVIEW;
  } catch {
    return CRITERION_STATUS.NOT_RUN;
  }
}

/**
 * criterionResults(criterion, resultsByCheckId) -> [{ checkId, result|null }]
 * for each of the criterion's checkIds — the per-check breakdown a detail
 * card renders alongside the rolled-up status. Never throws.
 */
export function criterionResults(criterion, resultsByCheckId) {
  const checkIds = Array.isArray(criterion?.checkIds) ? criterion.checkIds.filter(Boolean) : [];
  return checkIds.map((checkId) => ({ checkId, result: lookupResult(resultsByCheckId, checkId) }));
}

/**
 * Build a { met, notMet, needsReview, manualReview, notRun } rollup across a
 * launch's whole criteria[] — used for the compact summary strip above the
 * per-criterion cards.
 */
export function launchRollup(criteria, resultsByCheckId) {
  const out = { met: 0, notMet: 0, needsReview: 0, manualReview: 0, notRun: 0 };
  for (const c of Array.isArray(criteria) ? criteria : []) {
    const s = criterionStatus(c, resultsByCheckId);
    if (s === CRITERION_STATUS.MET) out.met += 1;
    else if (s === CRITERION_STATUS.NOT_MET) out.notMet += 1;
    else if (s === CRITERION_STATUS.NEEDS_REVIEW) out.needsReview += 1;
    else if (s === CRITERION_STATUS.MANUAL_REVIEW) out.manualReview += 1;
    else out.notRun += 1;
  }
  return out;
}

export default criterionStatus;
